import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import styles from './ResourceDetail.module.css';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ResourceDetail() {
  const { currentUser } = useAuth();
  const { id } = useParams();

  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        const res = await fetch(`${API_URL}/resources/${id}`);
        if (!res.ok) throw new Error('Resource not found');
        const data = await res.json();
        console.log('Fetched resource:', data);
        setResource(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [id]);

  // Check saved status
  useEffect(() => {
    if (!currentUser || !resource) return;
    const checkSaved = async () => {
      try {
        const res = await fetch(`${API_URL}/resources/saved`, {
          headers: { 'x-user-id': currentUser.uid }
        });
        const saved = await res.json();
        setIsSaved(saved.some(r => r.id === resource.id));
      } catch (e) {
        console.error(e);
      }
    };
    checkSaved();
  }, [currentUser, resource]);

  const handleToggleSave = async () => {
    if (!currentUser) {
      alert('Please sign in to save resources');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/resources/${resource.id}/toggle-save`, {
        method: 'POST',
        headers: { 'x-user-id': currentUser.uid }
      });

      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setIsSaved(data.saved);
    } catch (err) {
      alert('Failed to update saved status');
    }
  };

  const handleDownload = async () => {
    if (!resource?.downloadUrl) {
      alert("This resource is not available for download yet.");
      return;
    }

    setDownloading(true);
    try {
      const link = document.createElement('a');
      link.href = resource.downloadUrl;
      link.download = resource.title.replace(/[^a-zA-Z0-9.-]/g, '_') + 
                      (resource.fileType ? `.${resource.fileType.split('/').pop()}` : '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Download failed. Please try again later.');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: resource.title,
      text: resource.description?.substring(0, 100) + '...',
      url: window.location.href,
    };

    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      alert('Could not share');
    }
  };

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Icon icon="lucide:loader-2" className={styles.spinner} />
            Loading resource...
          </div>
        </div>
      </main>
    );
  }

  if (error || !resource) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            <Icon icon="lucide:alert-triangle" />
            {error || 'Resource not found'}
          </div>
          <Link to="/collections" className={styles.backLink}>← Back to Collections</Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <nav className={styles.breadcrumb}>
          <Link to="/" className={styles.breadLink}>Home</Link>
          <span>→</span>
          <Link to="/collections" className={styles.breadLink}>Collections</Link>
          <span>→</span>
          <span className={styles.current}>{resource.title}</span>
        </nav>

        <header className={styles.resourceHeader}>
          <div className={styles.headerMeta}>
            <span className={styles.typeTag}>{resource.type}</span>
            <span className={styles.categoryTag}>{resource.category || 'General'}</span>
            <span className={styles.updated}>
              Updated {new Date(resource.updatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          <h1 className={styles.resourceTitle}>{resource.title}</h1>
          <p className={styles.resourceSubtitle}>{resource.description}</p>

          <div className={styles.headerActions}>
            <button 
              className={styles.downloadBtn} 
              onClick={handleDownload}
              disabled={downloading || !resource.downloadUrl}
            >
              <Icon icon={downloading ? "lucide:loader-2" : "lucide:download"} />
              {downloading ? 'Downloading...' : `Download ${resource.fileSize ? `(${resource.fileSize})` : ''}`}
            </button>

            <button className={styles.actionBtn} onClick={handleToggleSave}>
              <Icon icon={isSaved ? "lucide:bookmark-check" : "lucide:bookmark-plus"} />
              {isSaved ? 'Saved' : 'Save to My Resources'}
            </button>

            <button className={styles.actionBtn} onClick={handleShare}>
              <Icon icon="lucide:share-2" />
              Share
            </button>
          </div>

          <div className={styles.resourceMeta}>
            <div className={styles.metaItem}>
              <Icon icon="lucide:star" />
              <span>{resource.rating ? `${resource.rating} (${resource.reviewCount || 0})` : 'No ratings yet'}</span>
            </div>
            <div className={styles.metaItem}>
              <Icon icon="lucide:globe" />
              <span>{resource.languages?.join(', ') || 'Not specified'}</span>
            </div>
            <div className={styles.metaItem}>
              <Icon icon="lucide:user" />
              <span>From: {resource.source || 'Community'}</span>
            </div>
          </div>
        </header>

        {/* Rest of your content (description, tags, sidebar) remains unchanged */}
        <section className={styles.contentSection}>
          <div className={styles.contentMain}>
            <h2 className={styles.sectionHeading}>Full Description</h2>
            <div className={styles.longDescription}>
              {resource.longDescription || resource.description?.split('\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            <h2 className={styles.sectionHeading}>Tags</h2>
            <div className={styles.tagsList}>
              {resource.tags?.map((tag, i) => (
                <span key={i} className={styles.detailTag}>{tag}</span>
              )) || <p>No tags available</p>}
            </div>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>Related Resources</h3>
              <ul className={styles.relatedList}>
                {resource.related?.map((rel, i) => (
                  <li key={i}>
                    <Link to={rel.link || `/resource/${rel.id}`} className={styles.relatedLink}>
                      {rel.title}
                    </Link>
                  </li>
                )) || <li>No related resources yet</li>}
              </ul>
              <Link to="/collections" className={styles.viewAllRelated}>
                View All Related →
              </Link>
            </div>

            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>Quick Actions</h3>
              <button className={styles.sidebarBtn}>
                <Icon icon="lucide:printer" />
                Print Version
              </button>
              <button className={styles.sidebarBtn}>
                <Icon icon="lucide:bookmark" />
                Add to Collection
              </button>
            </div>
          </aside>
        </section>
        {/* ... */}
      </div>
    </main>
  );
}

//ends here

// import { useState, useEffect } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './ResourceDetail.module.css';
// import { useAuth } from '../../context/AuthContext';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// export default function ResourceDetail() {
//   const { currentUser } = useAuth();
//   const { id } = useParams();

//   const [resource, setResource] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [isSaved, setIsSaved] = useState(false);
//   const [downloading, setDownloading] = useState(false);

//   useEffect(() => {
//     const fetchResource = async () => {
//       try {
//         const res = await fetch(`${API_URL}/resources/${id}`);
//         if (!res.ok) throw new Error(`Resource not found`);
//         const data = await res.json();
//         setResource(data);
//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchResource();
//   }, [id]);

//   // Check if already saved
//   useEffect(() => {
//     if (!currentUser || !resource) return;

//     const checkSavedStatus = async () => {
//       try {
//         const res = await fetch(`${API_URL}/resources/saved`, {
//           headers: { 'x-user-id': currentUser.uid }
//         });
//         const savedList = await res.json();
//         setIsSaved(savedList.some(r => r.id === resource.id));
//       } catch (err) {
//         console.error('Failed to check saved status');
//       }
//     };

//     checkSavedStatus();
//   }, [currentUser, resource]);

//   const handleToggleSave = async () => {
//     if (!currentUser) {
//       alert('Please sign in to save resources');
//       return;
//     }

//     try {
//       const res = await fetch(`${API_URL}/resources/${resource.id}/toggle-save`, {
//         method: 'POST',
//         headers: { 'x-user-id': currentUser.uid }
//       });

//       if (!res.ok) throw new Error('Toggle failed');

//       const data = await res.json();
//       setIsSaved(data.saved);
//       alert(data.message); // You can replace with toast later
//     } catch (err) {
//       console.error(err);
//       alert('Failed to update saved status');
//     }
//   };

//   const handleDownload = async () => {
//     if (!resource?.fileUrl) {
//       alert("Sorry, this resource doesn't have a downloadable file yet.");
//       return;
//     }

//     setDownloading(true);
//     try {
//       const link = document.createElement('a');
//       link.href = resource.fileUrl;
//       link.download = resource.fileUrl.split('/').pop() || 'resource';
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);

//       // Optional: Track download (future analytics)
//       console.log(`Downloaded: ${resource.title}`);
//     } catch (err) {
//       console.error(err);
//       alert('Failed to download file. Please try again.');
//     } finally {
//       setDownloading(false);
//     }
//   };

//   const handleShare = async () => {
//     if (!resource) return;

//     const shareData = {
//       title: resource.title,
//       text: resource.description?.substring(0, 100) + '...',
//       url: window.location.href,
//     };

//     try {
//       if (navigator.share) {
//         await navigator.share(shareData);
//       } else {
//         await navigator.clipboard.writeText(window.location.href);
//         alert('Link copied to clipboard!');
//       }
//     } catch (err) {
//       console.error(err);
//       alert('Could not share resource');
//     }
//   };

//   if (loading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>
//             <Icon icon="lucide:loader-2" className={styles.spinner} />
//             Loading resource...
//           </div>
//         </div>
//       </main>
//     );
//   }

//   if (error || !resource) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             <Icon icon="lucide:alert-triangle" />
//             {error || 'Resource not found'}
//           </div>
//           <Link to="/collections" className={styles.backLink}>
//             ← Back to Collections
//           </Link>
//         </div>
//       </main>
//     );
//   }

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Breadcrumb */}
//         <nav className={styles.breadcrumb}>
//           <Link to="/" className={styles.breadLink}>Home</Link>
//           <span>→</span>
//           <Link to="/collections" className={styles.breadLink}>Collections</Link>
//           <span>→</span>
//           <span className={styles.current}>{resource.title}</span>
//         </nav>

//         {/* Resource Header */}
//         <header className={styles.resourceHeader}>
//           <div className={styles.headerMeta}>
//             <span className={styles.typeTag}>{resource.type}</span>
//             <span className={styles.categoryTag}>{resource.category || 'General'}</span>
//             <span className={styles.updated}>
//               Updated {new Date(resource.updatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
//             </span>
//           </div>

//           <h1 className={styles.resourceTitle}>{resource.title}</h1>
//           <p className={styles.resourceSubtitle}>{resource.description}</p>

//           <div className={styles.headerActions}>
//             <button 
//               className={styles.downloadBtn} 
//               onClick={handleDownload}
//               disabled={downloading || !resource.fileUrl}
//             >
//               <Icon icon={downloading ? "lucide:loader-2" : "lucide:download"} />
//               {downloading ? 'Downloading...' : `Download ${resource.fileSize ? `(${resource.fileSize})` : ''}`}
//             </button>

//             <button className={styles.actionBtn} onClick={handleToggleSave}>
//               <Icon icon={isSaved ? "lucide:bookmark-check" : "lucide:bookmark-plus"} />
//               {isSaved ? 'Saved' : 'Save to My Resources'}
//             </button>

//             <button className={styles.actionBtn} onClick={handleShare}>
//               <Icon icon="lucide:share-2" />
//               Share
//             </button>
//           </div>

//           <div className={styles.resourceMeta}>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:star" />
//               <span>{resource.rating ? `${resource.rating} (${resource.reviewCount || 0} reviews)` : 'No ratings yet'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:globe" />
//               <span>{resource.languages?.join(', ') || 'Not specified'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:user" />
//               <span>From: {resource.source || 'Community'}</span>
//             </div>
//           </div>
//         </header>

//         {/* Rest of your content remains the same */}
//         <section className={styles.contentSection}>
//           <div className={styles.contentMain}>
//             <h2 className={styles.sectionHeading}>Full Description</h2>
//             <div className={styles.longDescription}>
//               {resource.longDescription || resource.description?.split('\n').map((para, i) => (
//                 <p key={i}>{para}</p>
//               ))}
//             </div>

//             <h2 className={styles.sectionHeading}>Tags</h2>
//             <div className={styles.tagsList}>
//               {resource.tags?.map((tag, i) => (
//                 <span key={i} className={styles.detailTag}>{tag}</span>
//               )) || <p>No tags available</p>}
//             </div>
//           </div>

//           <aside className={styles.sidebar}>
//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Related Resources</h3>
//               <ul className={styles.relatedList}>
//                 {resource.related?.map((rel, i) => (
//                   <li key={i}>
//                     <Link to={rel.link || `/resource/${rel.id}`} className={styles.relatedLink}>
//                       {rel.title}
//                     </Link>
//                   </li>
//                 )) || <li>No related resources yet</li>}
//               </ul>
//               <Link to="/collections" className={styles.viewAllRelated}>
//                 View All Related →
//               </Link>
//             </div>

//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Quick Actions</h3>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:printer" />
//                 Print Version
//               </button>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:bookmark" />
//                 Add to Collection
//               </button>
//             </div>
//           </aside>
//         </section>
//       </div>
//     </main>
//   );
// }

// import { useState, useEffect } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './ResourceDetail.module.css';
// import { useAuth } from '../../context/AuthContext';  // ← import only

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// export default function ResourceDetail() {
 
//   const { currentUser } = useAuth();

//   const [resource, setResource] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [isSaved, setIsSaved] = useState(false); // initial state

//   const { id } = useParams();

//   useEffect(() => {
//     const fetchResource = async () => {
//       try {
//         const res = await fetch(`${API_URL}/resources/${id}`);
//         if (!res.ok) throw new Error(`HTTP ${res.status} - Resource not found`);
//         const data = await res.json();
//         setResource(data);
//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchResource();
//   }, [id]);

//   const handleToggleSave = async () => {
//     if (!currentUser) {
//       alert('Please sign in to save resources');
//       return;
//     }

//     try {
//       const res = await fetch(`${API_URL}/resources/${resource.id}/toggle-save`, {
//         method: 'POST',
//         headers: {
//           'x-user-id': currentUser.uid
//         }
//       });

//       if (!res.ok) throw new Error('Toggle failed');

//       const data = await res.json();
//       setIsSaved(data.saved);
//       alert(data.message);
//     } catch (err) {
//       console.error(err);
//       alert('Failed to update saved status');
//     }
//   };

//   if (loading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>
//             <Icon icon="lucide:loader-2" className={styles.spinner} />
//             Loading resource...
//           </div>
//         </div>
//       </main>
//     );
//   }

//   if (error || !resource) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             <Icon icon="lucide:alert-triangle" />
//             {error || 'Resource not found'}
//           </div>
//           <Link to="/collections" className={styles.backLink}>
//             ← Back to Collections
//           </Link>
//         </div>
//       </main>
//     );
//   }

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Breadcrumb / Back */}
//         <nav className={styles.breadcrumb}>
//           <Link to="/" className={styles.breadLink}>Home</Link>
//           <span>→</span>
//           <Link to="/collections" className={styles.breadLink}>Collections</Link>
//           <span>→</span>
//           <span className={styles.current}>{resource.title}</span>
//         </nav>

//         {/* Resource Header */}
//         <header className={styles.resourceHeader}>
//           <div className={styles.headerMeta}>
//             <span className={styles.typeTag}>{resource.type}</span>
//             <span className={styles.categoryTag}>{resource.category || 'General'}</span>
//             <span className={styles.updated}>
//               Updated {new Date(resource.updatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
//             </span>
//           </div>

//           <h1 className={styles.resourceTitle}>{resource.title}</h1>

//           <p className={styles.resourceSubtitle}>{resource.description}</p>

//           <div className={styles.headerActions}>
//             <button className={styles.downloadBtn}>
//               <Icon icon="lucide:download" />
//               Download {resource.fileSize ? `(${resource.fileSize})` : ''}
//             </button>

//             <button className={styles.actionBtn} onClick={handleToggleSave}>
//               <Icon icon={isSaved ? "lucide:bookmark-check" : "lucide:bookmark-plus"} />
//               {isSaved ? 'Saved' : 'Save to My Resources'}
//             </button>

//             <button className={styles.actionBtn}>
//               <Icon icon="lucide:share-2" />
//               Share
//             </button>
//           </div>

//           <div className={styles.resourceMeta}>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:star" />
//               <span>{resource.rating ? `${resource.rating} (${resource.reviewCount || 0} reviews)` : 'No ratings yet'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:globe" />
//               <span>{resource.languages?.join(', ') || 'Not specified'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:user" />
//               <span>From: {resource.source || 'Community'}</span>
//             </div>
//           </div>
//         </header>

//         {/* Main Content */}
//         <section className={styles.contentSection}>
//           <div className={styles.contentMain}>
//             <h2 className={styles.sectionHeading}>Full Description</h2>
//             <div className={styles.longDescription}>
//               {resource.longDescription || resource.description?.split('\n').map((para, i) => (
//                 <p key={i}>{para}</p>
//               ))}
//             </div>

//             <h2 className={styles.sectionHeading}>Tags</h2>
//             <div className={styles.tagsList}>
//               {resource.tags?.map((tag, i) => (
//                 <span key={i} className={styles.detailTag}>{tag}</span>
//               )) || <p>No tags available</p>}
//             </div>
//           </div>

//           {/* Sidebar – Related Resources */}
//           <aside className={styles.sidebar}>
//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Related Resources</h3>
//               <ul className={styles.relatedList}>
//                 {resource.related?.map((rel, i) => (
//                   <li key={i}>
//                     <Link to={rel.link || `/resource/${rel.id}`} className={styles.relatedLink}>
//                       {rel.title}
//                     </Link>
//                   </li>
//                 )) || <li>No related resources yet</li>}
//               </ul>
//               <Link to="/collections" className={styles.viewAllRelated}>
//                 View All Related →
//               </Link>
//             </div>

//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Quick Actions</h3>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:printer" />
//                 Print Version
//               </button>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:bookmark" />
//                 Add to Collection
//               </button>
//             </div>
//           </aside>
//         </section>
//       </div>
//     </main>
//   );
// }
// import { useState, useEffect } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './ResourceDetail.module.css';
// import { useAuth } from '../../context/AuthContext';
// const { currentUser } = useAuth();
// const [isSaved, setIsSaved] = useState(false); // initial state - fetch real later if needed

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// export default function ResourceDetail() {
//   const handleToggleSave = async () => {
//   if (!currentUser) {
//     alert('Please sign in to save resources');
//     return;
//   }

//   try {
//     const res = await fetch(`${API_URL}/resources/${resource.id}/toggle-save`, {
//       method: 'POST',
//       headers: {
//         'x-user-id': currentUser.uid
//       }
//     });

//     if (!res.ok) throw new Error('Toggle failed');

//     const data = await res.json();
//     setIsSaved(data.saved);
//     alert(data.message);
//   } catch (err) {
//     console.error(err);
//     alert('Failed to update saved status');
//   }
// };


//   const { id } = useParams(); // e.g. /resource/123e4567-...
//   const [resource, setResource] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchResource = async () => {
//       try {
//         const res = await fetch(`${API_URL}/resources/${id}`);
//         if (!res.ok) throw new Error(`HTTP ${res.status} - Resource not found`);
//         const data = await res.json();
//         setResource(data);
//       } catch (err) {
//         setError(err.message);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchResource();
//   }, [id]);

//   if (loading) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.loading}>
//             <Icon icon="lucide:loader-2" className={styles.spinner} />
//             Loading resource...
//           </div>
//         </div>
//       </main>
//     );
//   }

//   if (error || !resource) {
//     return (
//       <main className={styles.main}>
//         <div className={styles.container}>
//           <div className={styles.error}>
//             <Icon icon="lucide:alert-triangle" />
//             {error || 'Resource not found'}
//           </div>
//           <Link to="/collections" className={styles.backLink}>
//             ← Back to Collections
//           </Link>
//         </div>
//       </main>
//     );
//   }

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Breadcrumb / Back */}
//         <nav className={styles.breadcrumb}>
//           <Link to="/" className={styles.breadLink}>Home</Link>
//           <span>→</span>
//           <Link to="/collections" className={styles.breadLink}>Collections</Link>
//           <span>→</span>
//           <span className={styles.current}>{resource.title}</span>
//         </nav>

//         {/* Resource Header */}
//         <header className={styles.resourceHeader}>
//           <div className={styles.headerMeta}>
//             <span className={styles.typeTag}>{resource.type}</span>
//             <span className={styles.categoryTag}>{resource.category || 'General'}</span>
//             <span className={styles.updated}>
//               Updated {new Date(resource.updatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
//             </span>
//           </div>

//           <h1 className={styles.resourceTitle}>{resource.title}</h1>

//           <p className={styles.resourceSubtitle}>{resource.description}</p>

//           <div className={styles.headerActions}>
//             <button className={styles.downloadBtn}>
//               <Icon icon="lucide:download" />
//               Download {resource.fileSize ? `(${resource.fileSize})` : ''}
//             </button>
//             <button className={styles.actionBtn} onClick={handleToggleSave}>
//               <Icon icon="lucide:bookmark-plus" />
//               Save to My Resources
//             </button>
//             <button className={styles.actionBtn} >
//               <Icon icon="lucide:share-2" />
//               Share
//             </button>
//           </div>

//           <div className={styles.resourceMeta}>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:star" />
//               <span>{resource.rating ? `${resource.rating} (${resource.reviewCount || 0} reviews)` : 'No ratings yet'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:globe" />
//               <span>{resource.languages?.join(', ') || 'Not specified'}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:user" />
//               <span>From: {resource.source || 'Community'}</span>
//             </div>
//           </div>
//         </header>

//         {/* Main Content */}
//         <section className={styles.contentSection}>
//           <div className={styles.contentMain}>
//             <h2 className={styles.sectionHeading}>Full Description</h2>
//             <div className={styles.longDescription}>
//               {resource.longDescription || resource.description?.split('\n').map((para, i) => (
//                 <p key={i}>{para}</p>
//               ))}
//             </div>

//             <h2 className={styles.sectionHeading}>Tags</h2>
//             <div className={styles.tagsList}>
//               {resource.tags?.map((tag, i) => (
//                 <span key={i} className={styles.detailTag}>{tag}</span>
//               )) || <p>No tags available</p>}
//             </div>
//           </div>

//           {/* Sidebar – Related Resources */}
//           <aside className={styles.sidebar}>
//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Related Resources</h3>
//               <ul className={styles.relatedList}>
//                 {/* Backend should return related resources; fallback to empty */}
//                 {resource.related?.map((rel, i) => (
//                   <li key={i}>
//                     <Link to={rel.link || `/resource/${rel.id}`} className={styles.relatedLink}>
//                       {rel.title}
//                     </Link>
//                   </li>
//                 )) || <li>No related resources yet</li>}
//               </ul>
//               <Link to="/collections" className={styles.viewAllRelated}>
//                 View All Related →
//               </Link>
//             </div>

//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Quick Actions</h3>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:printer" />
//                 Print Version
//               </button>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:bookmark" />
//                 Add to Collection
//               </button>
//             </div>
//           </aside>
//         </section>
//       </div>
//     </main>
//   );
// }
// import { Link, useParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './ResourceDetail.module.css';

// // Dummy data – in real app fetched by ID from params
// const dummyResource = {
//   id: 'digital-checklist-2026',
//   title: "Digital Security Checklist for High-Risk Activists (2026 Edition)",
//   type: "Guide",
//   category: "Digital Security",
//   description: "Comprehensive step-by-step checklist covering device hardening, secure communication setup, threat modeling, password management, and safe online behavior for activists facing surveillance or physical risk.",
//   longDescription: `
//     This guide is designed for individuals and small teams operating in high-threat environments. It includes:

//     • Device security baselines (phones, laptops)
//     • Encrypted messaging and email recommendations
//     • Secure file storage and sharing
//     • Phishing & malware defense tactics
//     • Travel & border crossing digital hygiene
//     • Post-incident recovery steps

//     Updated in 2026 to address latest spyware threats (Pegasus-style), commercial surveillance tools, and AI-assisted monitoring.
//   `,
//   source: "Global Rights Foundation",
//   rating: "4.8 (142 reviews)",
//   languages: ["English", "Spanish", "French", "Arabic"],
//   fileSize: "2.4 MB PDF",
//   updated: "March 2026",
//   tags: ["Checklist", "High-Risk", "Device Security", "Encryption", "Threat Modeling"],
//   related: [
//     { title: "Organizational Security Policy Template", link: "/resource/policy-template" },
//     { title: "Emergency Digital Security Helpdesk Contacts", link: "/support" },
//     { title: "Secure Communication Tools Comparison", link: "/resource/comm-tools" }
//   ]
// };

// export default function ResourceDetail() {
//   const { id } = useParams(); // in real app use this to fetch correct resource

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Breadcrumb / Back */}
//         <nav className={styles.breadcrumb}>
//           <Link to="/" className={styles.breadLink}>Home</Link>
//           <span>→</span>
//           <Link to="/collections" className={styles.breadLink}>Collections</Link>
//           <span>→</span>
//           <span className={styles.current}>{dummyResource.title}</span>
//         </nav>

//         {/* Resource Header */}
//         <header className={styles.resourceHeader}>
//           <div className={styles.headerMeta}>
//             <span className={styles.typeTag}>{dummyResource.type}</span>
//             <span className={styles.categoryTag}>{dummyResource.category}</span>
//             <span className={styles.updated}>Updated {dummyResource.updated}</span>
//           </div>

//           <h1 className={styles.resourceTitle}>{dummyResource.title}</h1>

//           <p className={styles.resourceSubtitle}>{dummyResource.description}</p>

//           <div className={styles.headerActions}>
//             <button className={styles.downloadBtn}>
//               <Icon icon="lucide:download" />
//               Download ({dummyResource.fileSize})
//             </button>
//             <button className={styles.actionBtn}>
//               <Icon icon="lucide:bookmark-plus" />
//               Save to My Resources
//             </button>
//             <button className={styles.actionBtn}>
//               <Icon icon="lucide:share-2" />
//               Share
//             </button>
//           </div>

//           <div className={styles.resourceMeta}>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:star" />
//               <span>{dummyResource.rating}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:globe" />
//               <span>{dummyResource.languages.join(', ')}</span>
//             </div>
//             <div className={styles.metaItem}>
//               <Icon icon="lucide:user" />
//               <span>From: {dummyResource.source}</span>
//             </div>
//           </div>
//         </header>

//         {/* Main Content */}
//         <section className={styles.contentSection}>
//           <div className={styles.contentMain}>
//             <h2 className={styles.sectionHeading}>Full Description</h2>
//             <div className={styles.longDescription}>
//               {dummyResource.longDescription.split('\n').map((para, i) => (
//                 <p key={i}>{para}</p>
//               ))}
//             </div>

//             <h2 className={styles.sectionHeading}>Tags</h2>
//             <div className={styles.tagsList}>
//               {dummyResource.tags.map((tag, i) => (
//                 <span key={i} className={styles.detailTag}>{tag}</span>
//               ))}
//             </div>
//           </div>

//           {/* Sidebar – Related Resources */}
//           <aside className={styles.sidebar}>
//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Related Resources</h3>
//               <ul className={styles.relatedList}>
//                 {dummyResource.related.map((rel, i) => (
//                   <li key={i}>
//                     <Link to={rel.link} className={styles.relatedLink}>
//                       {rel.title}
//                     </Link>
//                   </li>
//                 ))}
//               </ul>
//               <Link to="/collections" className={styles.viewAllRelated}>
//                 View All Related →
//               </Link>
//             </div>

//             <div className={styles.sidebarCard}>
//               <h3 className={styles.sidebarTitle}>Quick Actions</h3>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:printer" />
//                 Print Version
//               </button>
//               <button className={styles.sidebarBtn}>
//                 <Icon icon="lucide:bookmark" />
//                 Add to Collection
//               </button>
//             </div>
//           </aside>
//         </section>
//       </div>
//     </main>
//   );
// }
// import { Icon } from '@iconify/react';
// import styles from './ResourceDetail.module.css';

// export default function ResourceDetail() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         <nav className={styles.breadcrumb}>
//           <a href="#">Resources</a> → <a href="#">Digital Security</a> → Organizational Security Policy Template
//         </nav>

//         <article className={styles.article}>
//           <header className={styles.header}>
//             <div className={styles.meta}>
//               <span className={styles.tag}>Policy</span>
//               <span className={styles.date}>Updated March 2025</span>
//             </div>
//             <h1 className={styles.title}>Organizational Security Policy Template</h1>
//             <p className={styles.description}>
//               Customizable policy framework for implementing emergency protocols organization-wide.
//             </p>
//             <div className={styles.actions}>
//               <button className={styles.btnPrimary}>
//                 <Icon icon="lucide:download" /> Download
//               </button>
//               <button className={styles.btnSecondary}>
//                 <Icon icon="lucide:bookmark" /> Save
//               </button>
//               <button className={styles.btnSecondary}>
//                 <Icon icon="lucide:share-2" /> Share
//               </button>
//             </div>
//           </header>

//           <section className={styles.content}>
//             <h2>Overview 1</h2>
//             <p>
//               This template provides a comprehensive framework that organizations can adapt to their specific context...
//             </p>

//             {/* More sections would go here – you can add from original HTML */}
//           </section>

//           <aside className={styles.sidebar}>
//             <div className={styles.sidebarCard}>
//               <h3>Resource Details</h3>
//               <dl>
//                 <dt>Format</dt><dd>ZIP, 45MB</dd>
//                 <dt>Languages</dt><dd>EN, ES, FR, AR</dd>
//                 <dt>Rating</dt><dd>4.9 (203 reviews)</dd>
//               </dl>
//             </div>

//             <div className={styles.related}>
//               <h3>Related Resources</h3>
//               {/* 2–3 small cards */}
//             </div>
//           </aside>
//         </article>
//       </div>
//     </main>
//   );
// }